import {
  RightOutlined,
  PlusSquareOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { Cascader, Divider, Space, Spin } from 'antd';
import React, { Fragment, ReactNode, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppConfig } from '@/config/context';
import { getParamFromUrl } from '@/infrastructure/history';
import { getAppIcon } from '@/infrastructure/utils/app';
import { allowCreateProduct } from '@/presentation/components/allow-create-product';
import { CreateApp } from '@/presentation/components/pro-app-list/create-app';
import { useStores } from '@/presentation/store';
import { IProductInfo } from '@/service/pro-user';
import style from './index.module.scss';

interface SelectedOptionsType {
  key?: string | null;
  value?: string | null;
  label: ReactNode;
  pid?: number | null;
  appName?: string | null;
}

export const AppList = () => {
  const history = useHistory();
  const config = useAppConfig();
  const appRef = useRef<HTMLDivElement>(null);
  const { productId, product, products, loadProducts, changeProduct, loadDemoProducts } = useStores().ProductStore;
  const [loadStatus, setLoadStatus] = useState<undefined | 'loading' | 'loaded'>();
  const [visible, setVisible] = useState(false);
  const urlProductId = getParamFromUrl('productId');
  const isDemo = config.demoProduct?.some(item => item.productId === urlProductId);
  const onCascaderClick = async () => {
    if (!loadStatus) {
      setLoadStatus('loading');
      isDemo
        ? await loadDemoProducts() : await loadProducts();
      setLoadStatus('loaded');
    }
  };

  const labelRender = (app?: IProductInfo) => (app ? <div className={style.appNameItem}>
    {getAppIcon(`${app?.platformId}`, app?.isFlutter as boolean)}
    <span>{app?.productName ?? ''}</span>
  </div> : null);

  const options: SelectedOptionsType[] = products.map((app: IProductInfo) => ({
    key: app.productId,
    value: app.productId,
    appName: app.productName,
    pid: app.platformId,
    label: labelRender(app),
  }));

  const onChange: any = (_: string[], selectedOptions: SelectedOptionsType[]) => {
    const appId = selectedOptions[0].value;
    const { pid } = selectedOptions[0];
    if (!appId || !pid) {
      return;
    }
    changeProduct(appId, pid, '/overview');
  };

  const filter = (inputValue: string, path: any[]) => (
    path.some(option => option.appName?.toLowerCase()?.indexOf(inputValue.toLowerCase()) > -1)
  );

  const createApp = async () => {
    const isValues = await allowCreateProduct(history);
    if (!isValues) {
      return;
    }
    setVisible(true);
  };

  const dropdownRender = (menus: ReactNode) => {
    if (loadStatus !== 'loaded') {
      return <div className={style.spinBox}>
        <Spin />
      </div>;
    }

    return (
      <div className={style.appList}>
        <div className={style.appListTitle}>{isDemo ? '体验Demo' : '我参与的产品'}</div>
        {menus}
        <div className={style.appListItem} onClick={() => history.push('/workbench/apps')}>
          <Space>
            <AppstoreOutlined />
            <span>{isDemo ? '我的产品' : '全部产品'}</span>
          </Space>
          <RightOutlined className={style.suffixIcon} />
        </div>
        {!isDemo ? <Fragment>
          <Divider style={{ margin: '4px 0' }} />
          <div
            className={style.appListItem}
            onClick={createApp}
          >
            <Space>
              <PlusSquareOutlined />
              <span>创建产品</span>
            </Space>
            <RightOutlined className={style.suffixIcon} />
          </div>
        </Fragment> : null}
      </div>
    );
  };

  return (
    <div ref={appRef} className={style.productSelector}>
      <Cascader
        options={options}
        value={[productId]}
        allowClear={false}
        showSearch={{ filter }}
        getPopupContainer={() => appRef?.current as HTMLDivElement}
        style={{ minWidth: '200px' }}
        onChange={onChange}
        displayRender={() => labelRender(product as unknown as IProductInfo)}
        dropdownRender={dropdownRender}
        placeholder={'请选择产品'}
        onClick={onCascaderClick}
      />
      <CreateApp visible={visible} handleCancel={() => setVisible(false)} />
    </div>
  );
};
